// Preparación client-side de documentos para api/empleado/anexar (M.2B.2).
// La lógica pura (dimensiones, nombres, validación) está separada del Canvas
// para poder probarla sin navegador.

export const LADO_MAXIMO_FOTO = 2000;       // px del lado mayor tras procesar
export const CALIDAD_JPEG_FOTO = 0.8;
export const LIMITE_ARCHIVO_BYTES = 10 * 1024 * 1024; // 10 MB (archivo original manual / foto procesada)

// Selector manual: sólo lo que el backend tipifica por extensión (pdf/jpg/jpeg/png).
export const ACCEPT_ARCHIVO = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const EXTENSIONES_ARCHIVO = ['pdf', 'jpg', 'jpeg', 'png'];

// Reduce proporcionalmente para que el lado mayor no pase de `maximo`.
// Nunca amplía imágenes más pequeñas.
export function calcularDimensiones(ancho, alto, maximo = LADO_MAXIMO_FOTO) {
  const a = Math.max(1, Math.round(Number(ancho) || 0));
  const h = Math.max(1, Math.round(Number(alto) || 0));
  const mayor = Math.max(a, h);
  if (mayor <= maximo) return { ancho: a, alto: h };
  const escala = maximo / mayor;
  return { ancho: Math.max(1, Math.round(a * escala)), alto: Math.max(1, Math.round(h * escala)) };
}

const dos = n => String(n).padStart(2, '0');

// <Prefijo>_<ID_EMPLEADO>_<YYYYMMDD-HHmmss>.jpg con fecha/hora local del teléfono.
// Documentos usa "Foto"; Registro de capacitación usa "Capacitacion".
export function nombreFoto(idEmpleado, fecha = new Date(), prefijo = 'Foto') {
  const id = String(idEmpleado == null ? '' : idEmpleado).trim().replace(/[^A-Za-z0-9_-]/g, '_') || 'SIN_ID';
  const sello = `${fecha.getFullYear()}${dos(fecha.getMonth() + 1)}${dos(fecha.getDate())}-${dos(fecha.getHours())}${dos(fecha.getMinutes())}${dos(fecha.getSeconds())}`;
  return `${prefijo}_${id}_${sello}.jpg`;
}

export function formatearTamano(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

export function extension(nombre) {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(nombre || ''));
  return m ? m[1].toLowerCase() : '';
}

// Archivo elegido en "Subir archivo": se conserva tal cual (sin convertir).
// Devuelve null si es válido o el mensaje para el usuario.
export function validarArchivoManual(archivo) {
  if (!archivo) return 'No se seleccionó ningún archivo.';
  if (!EXTENSIONES_ARCHIVO.includes(extension(archivo.name))) {
    return 'Tipo de archivo no permitido. Usa PDF, JPG o PNG.';
  }
  if (archivo.size > LIMITE_ARCHIVO_BYTES) {
    return `El archivo pesa ${formatearTamano(archivo.size)} y supera el límite de ${formatearTamano(LIMITE_ARCHIVO_BYTES)} permitido en Mobile.`;
  }
  return null;
}

export function tipoLegible(nombre) {
  const ext = extension(nombre);
  if (ext === 'pdf') return 'PDF';
  if (ext === 'jpg' || ext === 'jpeg') return 'Imagen JPEG';
  if (ext === 'png') return 'Imagen PNG';
  return ext ? ext.toUpperCase() : 'Archivo';
}

// "data:<tipo>;base64,AAAA" -> "AAAA" (el backend espera base64 sin prefijo, igual que Desktop).
export function quitarPrefijoDataUrl(dataUrl) {
  const texto = String(dataUrl || '');
  const i = texto.indexOf(',');
  return texto.startsWith('data:') && i >= 0 ? texto.slice(i + 1) : texto;
}

// ─── Canvas (sólo navegador) ────────────────────────────────────────────────
function cargarImagen(archivo) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer la imagen.')); };
    img.src = url;
  });
}

// Foto de cámara -> JPEG normalizado (lado mayor ≤ 2000 px, calidad 0.80).
// Los navegadores actuales aplican la orientación EXIF al dibujar <img>.
export async function procesarFoto(archivo) {
  const img = await cargarImagen(archivo);
  const original = { ancho: img.naturalWidth || img.width, alto: img.naturalHeight || img.height };
  const destino = calcularDimensiones(original.ancho, original.alto);
  const canvas = document.createElement('canvas');
  canvas.width = destino.ancho;
  canvas.height = destino.alto;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // PNG con transparencia -> fondo blanco legible
  ctx.fillRect(0, 0, destino.ancho, destino.alto);
  ctx.drawImage(img, 0, 0, destino.ancho, destino.alto);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('No se pudo procesar la foto.'))), 'image/jpeg', CALIDAD_JPEG_FOTO);
  });
  return { blob, original, destino, tamanoOriginal: archivo.size, tipo: 'image/jpeg' };
}
